import {
  configureStore,
  ThunkAction,
  Action,
  combineReducers,
} from "@reduxjs/toolkit";
import taskReducer from "../features/tasks/taskSlice";
import authReducer from "../features/auth/authSlice";
import calendarReducer from "../features/calendar/calendarSlice";
import userReducer from "../features/users/users.slice";
import organizationReducer from "../features/organization/organization.slice";
import companionReducer from "../features/companion/companion.slice";
import settingsReducer from "../features/settings/settings.slice";
import objectivesReducer from "../features/objectives/objectives.slice";
import { hydrateFromCache, mergeCachedSlices } from "./offlineCache";

const appReducer = combineReducers({
  auth: authReducer,
  tasks: taskReducer,
  calendar: calendarReducer,
  user: userReducer,
  organizations: organizationReducer,
  companion: companionReducer,
  settings: settingsReducer,
  objectives: objectivesReducer,
});

type AppState = ReturnType<typeof appReducer>;

// 👇 Intercept logout here
// The return type breaks the type cycle through offlineCache
export const rootReducer = (
  state: AppState | undefined,
  action: Action
): AppState => {
  // You can match whatever your logout action type is, e.g.:
  if (
    action.type === "auth/logout" ||
    action.type === "auth/logout/fulfilled"
  ) {
    state = undefined; // flush all reducers
  }
  // Never over data already loaded from the server
  if (
    hydrateFromCache.match(action) &&
    state &&
    !state.tasks.meta.taskDataInitialized
  ) {
    return mergeCachedSlices(state, action.payload);
  }
  return appReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  devTools: {
    actionSanitizer: (action) =>
      action.type === "SET_TIMER_TIME"
        ? { ...action, payload: "<<OMITTED>>" }
        : action,
  },
});

// Define an error type if you expect errors to have a consistent shape
export interface ApiError {
  response?: {
    data?: {
      message: string;
    };
  };
  message: string;
}

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;

export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;

//store.dispatch(setActiveAccessRoles());
